import { LandType } from "@loanlink-nl/model-hdn/25.0/OfferteAanvraag";
import { asHdnType } from "@loanlink-nl/shared";
import { DateTime } from "luxon";
import { mergeCustomers, mergeIncome } from "../merge-dossier-data.js";
function convertLegitimatieSoort(brpType) {
    if (!brpType)
        return undefined;
    switch (brpType) {
        case "ED Elektronisch Identiteitsbewijs":
        case "EI Europese identiteitskaart":
        case "PP Paspoort":
            return brpType;
        default:
            return "PP Paspoort";
    }
}
function convertDILegitimatieSoort(diType) {
    if (!diType)
        return undefined;
    switch (diType) {
        case "ED Elektronisch Identiteitsbewijs":
        case "EI Europese identiteitskaart":
        case "PP Paspoort":
        case "RB Rijbewijs":
        case "VI Visum":
            return diType;
        default:
            return "PP Paspoort";
    }
}
function mapBurgerlijkeStaatToMaritalStatus(hypotheekgever) {
    if (!hypotheekgever)
        return {};
    let burgerlijkeStaat;
    if ("BurgerlijkeStaatGegevens" in hypotheekgever) {
        // HDN 26.0
        burgerlijkeStaat =
            hypotheekgever.BurgerlijkeStaatGegevens?.BurgerlijkeStaat;
    }
    else if ("BurgerlijkeStaat" in hypotheekgever) {
        // HDN 25.0
        burgerlijkeStaat = hypotheekgever.BurgerlijkeStaat;
    }
    switch (burgerlijkeStaat) {
        case "03 Alleenstaand":
            return { situation: "single" };
        case "01 Gehuwd (gemeenschap van goederen)":
            return {
                situation: "married",
                details: { terms: "shared property" },
            };
        case "02 Gehuwd met huwelijkse voorwaarden":
            return {
                situation: "married",
                details: { terms: "prenuptial agreement" },
            };
        case "09 Gehuwd in beperkte gemeenschap van goederen":
            return {
                situation: "married",
                details: { terms: "limited shared property" },
            };
        case "08 Gehuwd volgens buitenlands recht":
            return {
                situation: "married",
                details: { terms: "foregin law" },
            };
        case "06 Partnerregistratie in gemeenschap van goederen":
            return {
                situation: "partnership",
                details: { terms: "shared property" },
            };
        case "07 Partnerregistratie onder registratievoorwaarden":
            return {
                situation: "partnership",
                details: { terms: "prenuptial agreement" },
            };
        case "10 Partnerregistratie in beperkte gemeenschap van goederen":
            return {
                situation: "partnership",
                details: { terms: "limited shared property" },
            };
        case "04 Samenwonend met samenlevingscontract":
            return {
                situation: "coliving",
                details: { hasContract: true },
            };
        case "05 Samenwonend zonder samenlevingscontract":
            return {
                situation: "coliving",
                details: { hasContract: false },
            };
        default:
            return {};
    }
}
function mapBRPBronEntiteitToCustomer(bronEntiteit, customerId) {
    const nawData = bronEntiteit.PartijNAWData;
    const hypotheekgever = bronEntiteit.Hypotheekgever;
    const identificatie = hypotheekgever.Identificatie?.[0];
    const personDetails = {
        firstName: nawData.VoorNamen,
        initials: nawData.VoorLetters,
        infix: nawData.GeboorteTussenvoegsels,
        lastName: nawData.GeboorteAchternaam,
        birthDate: nawData.GeboorteDt,
        gender: identificatie?.LegitimatieGeslacht,
    };
    let housenumber = String(nawData.HuisNr);
    if (nawData.HuisNrToevoeging) {
        housenumber += ` ${nawData.HuisNrToevoeging}`;
    }
    const residence = {
        streetName: nawData.StraatNaam,
        postcode: nawData.Postcode,
        housenumber: housenumber,
        place: nawData.PlaatsNaam,
    };
    const identification = identificatie
        ? {
            type: convertLegitimatieSoort(identificatie.LegitimatieSoort),
            idNumber: identificatie.LegitimatieNr,
        }
        : {};
    const [mostRecentDivorce] = hypotheekgever.VoormaligeBurgerlijkeStaat?.sort((a, b) => DateTime.fromISO(b.HuwelijkOntbondenDt)
        .diff(DateTime.fromISO(a.HuwelijkOntbondenDt))
        .toMillis()) ?? [];
    const otherDetails = {
        birthPlace: nawData.GeboortePlaats,
        birthCountry: asHdnType(nawData.GeboorteLand, LandType),
        nationality: asHdnType(nawData.Nationaliteit, LandType),
        maritalStatus: mapBurgerlijkeStaatToMaritalStatus(hypotheekgever),
        formerMaritalStatus: mostRecentDivorce
            ? {
                hasFormerStatus: true,
                kind: mostRecentDivorce.SoortOntbonden === "04 Onbekend"
                    ? undefined
                    : mostRecentDivorce.SoortOntbonden,
                date: mostRecentDivorce.HuwelijkOntbondenDt,
            }
            : { hasFormerStatus: false },
    };
    return {
        id: customerId,
        personDetails,
        contactDetails: {},
        otherDetails,
        residence,
        identification,
    };
}
function mapDIBronEntiteitToCustomer(bronEntiteit, customerId) {
    const nawData = bronEntiteit.PartijNAWData;
    const hypotheekgever = bronEntiteit.Hypotheekgever;
    const identificatie = hypotheekgever.Identificatie?.[0];
    const personDetails = {
        firstName: nawData.VoorNamen,
        initials: nawData.VoorLetters,
        infix: nawData.GeboorteTussenvoegsels,
        lastName: nawData.GeboorteAchternaam,
        birthDate: nawData.GeboorteDt,
        gender: identificatie?.LegitimatieGeslacht,
    };
    const contactDetails = {
        email: hypotheekgever["E-mailadres"],
    };
    const identification = identificatie
        ? {
            type: convertDILegitimatieSoort(identificatie.LegitimatieSoort),
            idNumber: identificatie.LegitimatieNr,
            issueDate: identificatie.AfgifteDt,
            issuePlace: identificatie.UitgifteInstantie,
            issueCountry: asHdnType(nawData.Nationaliteit, LandType),
        }
        : {};
    return {
        id: customerId,
        personDetails,
        contactDetails,
        identification,
    };
}
function mapLoondienstToIncomeSection(inkomsten) {
    return inkomsten.map((inkomst, index) => {
        const loondienst = inkomst.InkomensbepalingLoondienst;
        return {
            sectionKey: "loondienst",
            id: `loondienst-${index + 1}`,
            values: {
                calculationMethod: "uwvVerzekeringsbericht",
                uwvToetsinkomen: Number.parseFloat(loondienst.Toetsinkomen || "0") || 0,
            },
        };
    });
}
function mapPensioenToIncomeSection(bron, maritalStatus) {
    const pensioenSections = [];
    const aowSections = [];
    const inkomsten = bron.Hypotheekgever?.Inkomsten ?? [];
    const birth = DateTime.fromISO(bron.PartijNAWData?.GeboorteDt ?? "");
    const isSingleForAOW = maritalStatus?.situation === "single";
    for (const inkomst of inkomsten) {
        if (inkomst.Pensioen) {
            for (const pensioen of inkomst.Pensioen) {
                if (pensioen.Details) {
                    for (const detail of pensioen.Details) {
                        let startDate;
                        let endDate;
                        if (birth.isValid) {
                            const { Jaren: startYears, Maanden: startMonths } = detail.IngangsLeeftijd;
                            startDate = birth.plus({
                                years: Number.parseInt(startYears, 10),
                                months: Number.parseInt(startMonths, 10),
                            });
                            if (detail.EindLeeftijd) {
                                const { Jaren: endYears, Maanden: endMonths } = detail.EindLeeftijd;
                                endDate = birth.plus({
                                    years: Number.parseInt(endYears, 10),
                                    months: Number.parseInt(endMonths, 10),
                                });
                            }
                        }
                        if (detail.AOWAlleenstaand || detail.AOWSamenwonend) {
                            aowSections.push({
                                sectionKey: "uitkering",
                                id: `uitkering-${aowSections.length + 1}`,
                                values: {
                                    percentageCounted: 1,
                                    grossYearlyBenefit: Number.parseFloat((isSingleForAOW
                                        ? detail.AOWAlleenstaand
                                        : detail.AOWSamenwonend) || "0"),
                                    uitkeringType: "09 AOW",
                                    startDate: startDate?.toISODate(),
                                    endDate: endDate?.toISODate(),
                                },
                            });
                        }
                        if (detail.PensioenInkomen) {
                            pensioenSections.push({
                                sectionKey: "pensioen",
                                id: `pensioen-${pensioenSections.length + 1}`,
                                values: {
                                    institution: detail.PensioenUitvoerder,
                                    grossYearlyPayment: (Number.parseFloat(detail.PensioenInkomen || "0") || 0) *
                                        12,
                                    percentageCounted: 1,
                                    pensionStartDate: startDate?.toISODate(),
                                    paymentEndDate: endDate?.toISODate(),
                                },
                            });
                        }
                    }
                }
            }
        }
    }
    return { pensioen: pensioenSections, uitkering: aowSections };
}
export function mapBrondataToCustomer(brondata, customerId) {
    let customer;
    if (brondata.BasisRegistratiePersonenBericht?.Bron) {
        const bron = brondata.BasisRegistratiePersonenBericht.Bron;
        customer = mapBRPBronEntiteitToCustomer(bron, customerId);
    }
    if (brondata.DigitaleIdentificatieBericht?.Bron) {
        const bron = brondata.DigitaleIdentificatieBericht.Bron;
        const diData = mapDIBronEntiteitToCustomer(bron, customer?.id || customerId);
        if ((diData.contactDetails || diData.identification) && customer) {
            const existingCustomer = customer;
            customer = {
                ...existingCustomer,
                contactDetails: {
                    ...existingCustomer.contactDetails,
                    ...diData.contactDetails,
                },
                identification: {
                    ...existingCustomer.identification,
                    ...diData.identification,
                },
            };
        }
    }
    return customer;
}
export function mapBrondataToIncome(brondata, customerId, maritalStatus) {
    const byCustomer = {
        customerId,
        sections: {
            loondienst: [],
            pensioen: [],
            uitkering: [],
            onderneming: [],
            overig: [],
        },
    };
    if (brondata.LoondienstInkomstenBericht?.Bron) {
        const bron = brondata.LoondienstInkomstenBericht.Bron;
        if (bron.Hypotheekgever?.Inkomsten) {
            const loondienstSections = mapLoondienstToIncomeSection(bron.Hypotheekgever.Inkomsten);
            byCustomer.sections.loondienst = loondienstSections;
        }
    }
    // Map Pensioen data
    if (brondata.PensioenOverzichtBericht?.Bron) {
        const bron = brondata.PensioenOverzichtBericht.Bron;
        if (bron.Hypotheekgever?.Inkomsten) {
            const { pensioen, uitkering } = mapPensioenToIncomeSection(bron, maritalStatus);
            byCustomer.sections.uitkering = uitkering;
            byCustomer.sections.pensioen = pensioen;
        }
    }
    return byCustomer;
}
export function mapBrondataToLoanLinkModel(brondata, customerId) {
    const customer = mapBrondataToCustomer(brondata, customerId);
    const maritalStatus = customer?.otherDetails.maritalStatus;
    const income = mapBrondataToIncome(brondata, customerId, maritalStatus);
    return {
        customers: customer ? [customer] : [],
        income: { byCustomer: [income] },
    };
}
export function mergeBrondataWithDossierData(existingData, brondata, customerId) {
    const brondataResult = mapBrondataToLoanLinkModel(brondata, customerId);
    return {
        ...existingData,
        customers: mergeCustomers(existingData.customers, brondataResult.customers),
        income: mergeIncome(existingData.income, brondataResult.income),
    };
}
